import React, { useEffect, useState } from 'react';
import {
  TextField,
  Button,
  Grid,
  Typography,
  Paper,
  Switch,
  FormControlLabel,
  Box,
  InputAdornment,
  IconButton,
  Snackbar,
  Alert,
  Container,
  Avatar,
  styled,
  alpha,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Settings as SettingsIcon
} from '@mui/icons-material';
import axios from 'axios';
import colors from 'colors/color';
import { EditIcon, SaveIcon } from 'lucide-react';
import { useTour } from 'context/TourContext';
import { useTranslation } from 'react-i18next';

// Styled Components
const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  maxWidth: 1000,
  margin: 'auto',
  marginTop: theme.spacing(3),
  borderRadius: 20,
  boxShadow: `0 20px 60px -12px ${alpha(colors.primary, 0.15)}`,
  background: 'linear-gradient(135deg, #ffffff 0%, #fafbff 100%)',
  border: `1px solid ${alpha(colors.primary, 0.08)}`,
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 12,
    backgroundColor: '#ffffff',
    transition: 'all 0.3s ease',
    '&:hover': {
      boxShadow: `0 4px 20px ${alpha(colors.primary, 0.08)}`,
    },
    '&.Mui-focused': {
      boxShadow: `0 0 0 3px ${alpha(colors.primary, 0.1)}`,
      '& fieldset': {
        borderColor: colors.primary,
        borderWidth: 2,
      },
    },
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: colors.primary,
  },
}));

const StyledButton = styled(Button)(({ theme, variant }) => ({
  borderRadius: 12,
  padding: theme.spacing(1.5, 3),
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '1rem',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  ...(variant === 'contained' && {
    background: `linear-gradient(135deg, ${colors.primary} 0%, ${alpha(colors.primary, 0.8)} 100%)`,
    boxShadow: `0 8px 25px ${alpha(colors.primary, 0.3)}`,
    '&:hover': {
      background: `linear-gradient(135deg, ${alpha(colors.primary, 0.9)} 0%, ${colors.primary} 100%)`,
      boxShadow: `0 12px 35px ${alpha(colors.primary, 0.4)}`,
      transform: 'translateY(-2px)',
    },
  }),
  ...(variant === 'outlined' && {
    borderColor: colors.secondary,
    color: colors.primary,
    borderWidth: 2,
    '&:hover': {
      borderColor: colors.primary,
      backgroundColor: alpha(colors.primary, 0.04),
      transform: 'translateY(-1px)',
    },
  }),
}));

const StyledTypography = styled(Typography)(({ theme }) => ({
  color: "#64748b",
  fontSize: "14px",
  fontFamily: "Poppins",
  fontWeight: 500,
  mb: 1,
}));

export default function Settings() {
  const { t } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const apiUrl = process.env.REACT_APP_API_URL;
  const userId = getUserIdFromAccessToken();
  const { resetAllTours } = useTour();

  const [smtpSettings, setSmtpSettings] = useState({
    EMAIL_HOST: '',
    EMAIL_PORT: '',
    EMAIL_HOST_USER: '',
    EMAIL_HOST_PASSWORD: '',
    EMAIL_USE_TLS: true,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    console.log('settings')
    fetchSettings()
  }, [])
  
  function getUserIdFromAccessToken() {
    const accessToken = localStorage.getItem("accessToken");
    if (!accessToken) {
      console.error("No access token found in local storage");
      return null;
    }

    try {
      const decodedToken = parseJwt(accessToken);
      const userId = decodedToken.user_id;
      return userId;
    } catch (error) {
      console.error("Failed to decode access token", error);
      return null;
    }
  }
  
  function parseJwt(token) {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map(function (c) {
          return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join("")
    );
    return JSON.parse(jsonPayload);
  }
  
  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setSmtpSettings(prevSettings => ({
      ...prevSettings,
      [name]: value,
    }));
  };

  const handleSwitchChange = (event) => {
    setSmtpSettings(prevSettings => ({
      ...prevSettings,
      EMAIL_USE_TLS: event.target.checked,
    }));
  };
  
  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    updateSettings();
  };

  const toggleShowPassword = () => {
    setShowPassword(!showPassword);
  };
  
  useEffect(() => {
    console.log("Updated SMTP Settings:", smtpSettings);
  }, [smtpSettings]);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${apiUrl}authentication/smtp-settings/user/${userId}/`, {
        headers: {
          "Content-type": "application/json",
          Authorization: "Bearer " + localStorage.getItem("accessToken"),
        },
      });

      const settings = {
        EMAIL_HOST: response.data.EMAIL_HOST || '',
        EMAIL_PORT: response.data.EMAIL_PORT || '',
        EMAIL_HOST_USER: response.data.EMAIL_HOST_USER || '',
        EMAIL_HOST_PASSWORD: response.data.EMAIL_HOST_PASSWORD || '',
        EMAIL_USE_TLS: response.data.EMAIL_USE_TLS || false,
      };

      setSmtpSettings(settings);
      console.log('Fetched SMTP Settings:', settings);
    } catch (error) {
      console.error('Error fetching SMTP settings:', error);
      showSnackbar(t('Error fetching settings'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const updateSettings = async () => {
    try {
      console.log('-->', smtpSettings)
      const response = await axios.patch(`${apiUrl}authentication/smtp-settings/update/`, smtpSettings, {
        headers: {
          "Content-type": "application/json",
          Authorization: "Bearer " + localStorage.getItem("accessToken"),
        },
      });
      console.log('Settings updated successfully', response.data);
      showSnackbar(t('Settings updated successfully'), 'success');
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating SMTP settings:', error);
      showSnackbar(t('Error updating settings'), 'error');
    }
  };
  
  const testSmtpSettings = async () => {
    try {
      const response = await axios.post(
        `${apiUrl}authentication/test-smtp/`,
        {},  // No body needed for this request
        {
          headers: {
            "Content-type": "application/json",
            Authorization: "Bearer " + localStorage.getItem("accessToken"),
          },
        }
      );
      console.log('Test email sent successfully', response.data);
      showSnackbar(t('Test email sent successfully'), 'success');
    } catch (error) {
      console.error('Error testing SMTP settings:', error);
      showSnackbar(t('Error testing SMTP settings'), 'error');
    }
  };
  
  const handleSaveClick = () => {
    updateSettings();
  };

  const handleResetTours = () => {
    resetAllTours();
    showSnackbar(t('All tours have been reset'), 'success');
  };

  const showSnackbar = (message, severity) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setOpenSnackbar(true);
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <StyledPaper elevation={0}>
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            mb: 4,
            p: 3,
          }}
        >
          <Avatar 
            sx={{ 
              bgcolor: colors.primary,
              width: 60,
              height: 60,
              mr: 3,
            }}
          >
            <SettingsIcon sx={{ fontSize: 30 }} />
          </Avatar>
          <Box>
            <Typography 
              variant="h3" 
              component="h1"
              sx={{
                fontWeight: 700,
                background: colors.primary,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 1,
                fontFamily: 'Poppins',
              }}
            >
              {t('Settings')}
            </Typography>
            <Typography 
              sx={{ 
                color: alpha(colors.primary, 0.7),
                fontWeight: 500,
                fontFamily:"Poppins"
              }}
            >
              {t('smtp_mg')}
            </Typography>
          </Box>
        </Box>

        <Box display="flex" justifyContent="flex-end" alignItems="center" sx={{ mb: 3 }}>
          {!isEditing && (
            <StyledButton
              variant="contained"
              onClick={handleEditClick}
            >
              {t('Edit Form')}
            </StyledButton>
          )}
          {isEditing && (
            <Box sx={{ display: 'flex', gap: 2 }}>
              <StyledButton 
                variant="outlined" 
                onClick={() => setIsEditing(false)}
              >
                {t('Cancel')}
              </StyledButton>
              <StyledButton 
                variant="contained" 
                onClick={handleSaveClick}
              >
                {t('Save')}
              </StyledButton>
            </Box>
          )}
        </Box>

        {!loading && (
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <StyledTypography>{t('SMTP Host')}</StyledTypography>
                <StyledTextField
                  fullWidth
                  name="EMAIL_HOST"
                  value={smtpSettings.EMAIL_HOST}
                  onChange={handleInputChange}
                  helperText={t('Example: http://smtp.gmail.com, http://smtp.office365.com')}
                  disabled={!isEditing}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12}>
                <StyledTypography>{t('SMTP Port')}</StyledTypography>
                <StyledTextField
                  fullWidth
                  name="EMAIL_PORT"
                  type="number"
                  value={smtpSettings.EMAIL_PORT || ''}
                  onChange={handleInputChange}
                  helperText={t('Common ports: 587 (TLS), 25, 465 (SSL)')}
                  disabled={!isEditing}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12}>
                <StyledTypography>{t('Email Username')}</StyledTypography>
                <StyledTextField
                  fullWidth
                  name="EMAIL_HOST_USER"
                  value={smtpSettings.EMAIL_HOST_USER}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  variant="outlined"
                  helperText={t('Your full email address, e.g., user@example.com')}
                />
              </Grid>
              <Grid item xs={12}>
                <StyledTypography>{t('Email Password')}</StyledTypography>
                <StyledTextField
                  fullWidth
                  name="EMAIL_HOST_PASSWORD"
                  type={showPassword ? 'text' : 'password'}
                  value={smtpSettings.EMAIL_HOST_PASSWORD}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  variant="outlined"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={toggleShowPassword} edge="end">
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={Boolean(smtpSettings.EMAIL_USE_TLS)}
                      onChange={(e) => setSmtpSettings(prev => ({...prev, EMAIL_USE_TLS: e.target.checked}))}
                      name="EMAIL_USE_TLS"
                      color="primary"
                      disabled={!isEditing}
                    />
                  }
                  label={t('Use TLS')}
                />
              </Grid>
              {isEditing && (
                <Grid item xs={12}>
                  <StyledButton 
                    variant="outlined" 
                    onClick={testSmtpSettings}
                    sx={{ mt: 2 }}
                  >
                    {t('Test SMTP Settings')}
                  </StyledButton>
                </Grid>
              )}
            </Grid>
          </form>
        )}
      </StyledPaper>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbarSeverity} 
          sx={{ 
            width: "100%",
            borderRadius: 2,
            boxShadow: `0 8px 32px ${alpha(colors.primary, 0.15)}`,
            '& .MuiAlert-icon': {
              color: snackbarSeverity === 'success' ? colors.success : colors.error,
            },
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
}